import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  TrendingDown, TrendingUp, Minus, Activity, ShieldCheck, Sparkles, ArrowRight, LineChart as LineIcon,
} from "lucide-react";

// Static chart config — pulled out of render so recharts doesn't see new object refs each frame
const TREND_CHART_MARGIN = { top: 8, right: 12, bottom: 4, left: -12 };
const TREND_AXIS_TICK = { fontFamily: "monospace", fontSize: 10 };
const TREND_AXIS_LINE = { stroke: "#000" };
const TREND_TOOLTIP_STYLE = { fontFamily: "monospace", fontSize: 11, border: "2px solid black", borderRadius: 0 };
const TREND_DOT_BLUE = { r: 3, fill: "#0057ff" };
const TREND_DOT_RED = { r: 3, fill: "#FF2B00" };
const TREND_ACTIVE_DOT = { r: 5 };
const TREND_MONTH_LABELS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TREND_TOOLTIP_FORMATTER = (v, name) => [
  `${Math.round(v)}${name === "stress" ? "%" : "/100"}`,
  name === "score" ? "Wellness" : "Stress",
];

function scoreTone(score) {
  if (score >= 80) return { color: "var(--success-green, #16a34a)", label: "EXCELLENT" };
  if (score >= 60) return { color: "#0057ff", label: "HEALTHY" };
  if (score >= 40) return { color: "#d97706", label: "WATCH" };
  return { color: "var(--alert-red, #FF2B00)", label: "CRITICAL" };
}

function stressTone(pct) {
  if (pct >= 70) return "var(--alert-red, #FF2B00)";
  if (pct >= 45) return "#d97706";
  return "var(--success-green, #16a34a)";
}

// Return {href, chip} describing where to jump for a given sub-bar.
function subBarTarget(kind, sub) {
  if (kind === "BUDGET") {
    const w = sub.weakest;
    if (w && w.category) {
      return {
        href: "/budgets",
        chip: `${w.category} · ${Math.round(w.score)}`,
      };
    }
    return { href: "/budgets", chip: null };
  }
  if (kind === "GOALS") {
    const w = sub.weakest;
    if (w && w.name) return { href: "/goals", chip: `${w.name} · ${Math.round(w.progress)}%` };
    return { href: "/goals", chip: null };
  }
  // SAVINGS
  return { href: "/income", chip: sub.hint || null };
}

function SubBar({ label, kind, sub }) {
  const navigate = useNavigate();
  const tone = scoreTone(sub.score);
  const target = subBarTarget(kind, sub);
  return (
    <button
      type="button"
      onClick={() => navigate(target.href)}
      data-testid={`wellness-sub-${kind.toLowerCase()}`}
      className="text-left w-full group transition-transform hover:-translate-y-[1px]"
      title="Jump to fix this"
    >
      <div className="flex justify-between items-baseline mb-1 gap-2">
        <span className="overline">
          {label} <span className="opacity-60">/ {Math.round(sub.weight * 100)}%</span>
        </span>
        <div className="flex items-center gap-2">
          {target.chip && (
            <span
              className="hidden sm:inline-block font-mono text-[10px] px-1.5 py-0.5 border border-black/30 group-hover:bg-black group-hover:text-white transition-colors truncate max-w-[180px]"
              data-testid={`wellness-sub-chip-${kind.toLowerCase()}`}
            >
              FIX · {target.chip}
            </span>
          )}
          <span className="font-mono font-bold text-sm" style={{ color: tone.color }}>
            {Math.round(sub.score)}
          </span>
          <ArrowRight
            size={12}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      </div>
      <div className="h-2 bg-black/10 relative overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${Math.max(2, Math.min(100, sub.score))}%`, background: tone.color }}
        />
      </div>
    </button>
  );
}

function WellnessCard({ data, coachNote, loadingNote }) {
  const s = data.wellness;
  const tone = scoreTone(s.score);
  const circumference = 2 * Math.PI * 54;
  const dash = (s.score / 100) * circumference;
  return (
    <div className="bg-white border-2 border-black p-6 flex flex-col" data-testid="wellness-card">
      <div className="overline flex items-center gap-2 mb-3">
        <ShieldCheck size={12} /> FINANCIAL WELLNESS SCORE
      </div>
      <div className="flex items-center gap-6">
        <div className="relative shrink-0" style={{ width: 128, height: 128 }}>
          <svg width="128" height="128" viewBox="0 0 128 128">
            <circle cx="64" cy="64" r="54" fill="none" stroke="#0000000f" strokeWidth="12" />
            <circle
              cx="64" cy="64" r="54" fill="none"
              stroke={tone.color} strokeWidth="12"
              strokeDasharray={`${dash} ${circumference}`}
              strokeDashoffset="0" strokeLinecap="butt"
              transform="rotate(-90 64 64)"
              data-testid="wellness-ring"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div
              className="font-display font-black text-4xl tracking-tighter leading-none"
              style={{ color: tone.color }}
              data-testid="wellness-score-value"
            >
              {Math.round(s.score)}
            </div>
            <div className="font-mono text-[10px] opacity-60 mt-1">/ 100</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-display font-bold text-lg tracking-tight"
            style={{ color: tone.color }}
            data-testid="wellness-score-label"
          >
            {tone.label}
          </div>
          <p className="font-mono text-xs text-[var(--text-secondary)] mt-1 leading-relaxed">
            Composite of budget health, goal progress and savings resilience.
          </p>
        </div>
      </div>

      <div
        className="mt-4 bg-[#111111] text-white p-4 border border-black"
        data-testid="wellness-coach-note"
      >
        <div className="overline text-white/60 flex items-center gap-2">
          <Sparkles size={12} /> COACH · WHY IT MOVED
        </div>
        <div className="font-mono text-xs leading-relaxed mt-2 min-h-[42px]">
          {loadingNote ? (
            <>
              <span className="text-[var(--alert-red)] blink">█</span>{" "}
              <span className="text-white/70">reading your data…</span>
            </>
          ) : coachNote ? (
            <>
              <span className="text-[var(--alert-red)]">█</span> {coachNote}
            </>
          ) : (
            <span className="text-white/60">Note unavailable right now.</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 mt-5">
        <SubBar label="BUDGET" kind="BUDGET" sub={s.budget} />
        <SubBar label="GOALS" kind="GOALS" sub={s.goal} />
        <SubBar label="SAVINGS" kind="SAVINGS" sub={s.savings} />
      </div>
    </div>
  );
}

function StressCard({ data }) {
  const st = data.stress;
  const color = stressTone(st.current);
  const trending = st.trend;
  const TrendIcon = trending === "decreasing" ? TrendingDown : trending === "increasing" ? TrendingUp : Minus;
  const trendColor =
    trending === "decreasing"
      ? "var(--success-green, #16a34a)"
      : trending === "increasing"
      ? "var(--alert-red, #FF2B00)"
      : "var(--text-secondary)";
  const deltaSign = st.delta > 0 ? "+" : "";
  return (
    <div className="bg-white border-2 border-black p-6 flex flex-col" data-testid="stress-card">
      <div className="overline flex items-center gap-2 mb-3">
        <Activity size={12} /> FINANCIAL STRESS INDICATOR
      </div>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="font-mono text-sm uppercase tracking-wider text-[var(--text-secondary)]">
          Financial Stress
        </span>
        <span
          className="inline-flex items-center gap-1 font-display font-bold text-lg"
          style={{ color: trendColor }}
          data-testid="stress-trend-arrow"
        >
          <TrendIcon size={20} strokeWidth={3} />
        </span>
        <span
          className="font-display font-black text-4xl tracking-tighter"
          style={{ color }}
          data-testid="stress-value"
        >
          {Math.round(st.current)}%
        </span>
      </div>
      <div className="font-mono text-xs mt-2" data-testid="stress-delta" style={{ color: trendColor }}>
        {trending === "flat" ? (
          <>no change since last month</>
        ) : (
          <>
            {deltaSign}
            {Math.round(st.delta)}% since last month
          </>
        )}
      </div>

      <div className="mt-5 space-y-2">
        <div className="overline opacity-70">CONTRIBUTING FACTORS</div>
        {st.factors.map((f) => {
          const factorColor = stressTone(f.value);
          return (
            <div key={f.key} data-testid={`stress-factor-${f.key}`} className="flex items-center gap-3">
              <div className="w-40 shrink-0 font-mono text-xs">
                {f.label}
                <span className="opacity-50"> · {Math.round(f.weight * 100)}%</span>
              </div>
              <div className="flex-1 h-1.5 bg-black/10 relative overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{ width: `${Math.max(2, Math.min(100, f.value))}%`, background: factorColor }}
                />
              </div>
              <div className="w-10 text-right font-mono text-xs font-bold" style={{ color: factorColor }}>
                {Math.round(f.value)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendCard({ trend }) {
  if (!trend || trend.length === 0) {
    return (
      <div className="bg-white border-2 border-black p-6" data-testid="wellness-trend-loading">
        <div className="overline flex items-center gap-2 mb-3">
          <LineIcon size={12} /> WELLNESS TREND · LAST 6 MONTHS
        </div>
        <div className="font-mono text-xs text-[var(--text-secondary)]">Loading history…</div>
      </div>
    );
  }
  const latest = trend[trend.length - 1];
  const earliest = trend[0];
  const change = latest.score - earliest.score;
  const changeColor =
    change > 0.5 ? "var(--success-green, #16a34a)" : change < -0.5 ? "var(--alert-red, #FF2B00)" : "var(--text-secondary)";
  const label = (m) => {
    const [, mm] = m.split("-");
    return TREND_MONTH_LABELS[parseInt(mm, 10)];
  };
  return (
    <div className="bg-white border-2 border-black p-6" data-testid="wellness-trend-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2">
            <LineIcon size={12} /> WELLNESS TREND · LAST {trend.length} MONTHS
          </div>
          <div className="mt-1 font-mono text-xs text-[var(--text-secondary)]">
            Wellness score & stress % over time
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] opacity-60 tracking-wider">CHANGE VS {label(earliest.month).toUpperCase()}</div>
          <div className="font-display font-black text-2xl tracking-tighter" style={{ color: changeColor }} data-testid="wellness-trend-change">
            {change > 0 ? "+" : ""}{Math.round(change)}
          </div>
        </div>
      </div>
      <div className="mt-4 h-40" data-testid="wellness-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend.map((t) => ({ ...t, m: label(t.month) }))} margin={TREND_CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#0000000f" />
            <XAxis dataKey="m" tick={TREND_AXIS_TICK} axisLine={TREND_AXIS_LINE} tickLine={false} />
            <YAxis domain={[0, 100]} tick={TREND_AXIS_TICK} axisLine={false} tickLine={false} width={28} />
            <Tooltip
              contentStyle={TREND_TOOLTIP_STYLE}
              formatter={TREND_TOOLTIP_FORMATTER}
            />
            <Line type="monotone" dataKey="score" stroke="#0057ff" strokeWidth={2.5} dot={TREND_DOT_BLUE} activeDot={TREND_ACTIVE_DOT} />
            <Line type="monotone" dataKey="stress" stroke="#FF2B00" strokeWidth={2.5} dot={TREND_DOT_RED} activeDot={TREND_ACTIVE_DOT} strokeDasharray="4 4" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 mt-2 font-mono text-[10px]">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-[#0057ff]" /> WELLNESS</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-[#FF2B00]" style={{ borderTop: "1px dashed #FF2B00" }} /> STRESS</span>
      </div>
    </div>
  );
}

export default function WellnessSection({ data, coachNote, loadingNote, trend }) {
  if (!data) {
    return (
      <div className="font-mono text-sm text-[var(--text-secondary)]" data-testid="wellness-loading">
        LOADING WELLNESS…
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="wellness-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WellnessCard data={data} coachNote={coachNote} loadingNote={loadingNote} />
        <StressCard data={data} />
      </div>
      <TrendCard trend={trend} />
    </div>
  );
}
